import type { BuiltInNodeTemplate } from "./types"

const viewerCode = `import React from "react"
import * as THREE from "three"
import { OrbitControls } from "three/addons/controls/OrbitControls.js"

type MeshData = {
	vertices: number[]
	indices?: number[]
	normals?: number[]
	geometry_type?: string
	geometryType?: string
}

const collectMeshes = (value: unknown, meshes: MeshData[] = []): MeshData[] => {
	if (Array.isArray(value)) {
		for (const item of value) collectMeshes(item, meshes)
		return meshes
	}
	if (!value || typeof value !== "object") return meshes
	const record = value as Record<string, unknown>
	const mesh = record.mesh_data ?? record.meshData
	if (mesh && typeof mesh === "object" && Array.isArray((mesh as MeshData).vertices)) {
		meshes.push(mesh as MeshData)
		return meshes
	}
	if (Array.isArray(record.vertices)) {
		meshes.push(record as MeshData)
		return meshes
	}
	for (const item of Object.values(record)) collectMeshes(item, meshes)
	return meshes
}

const COLORS = ["#4a9eff", "#ff6b6b", "#51cf66", "#ffd43b", "#cc5de8", "#ff922b"]

const Viewer = ({ geometry }: { geometry?: unknown }) => {
	const containerRef = React.useRef<HTMLDivElement>(null)

	React.useEffect(() => {
		const container = containerRef.current
		if (!container) return

		const scene = new THREE.Scene()
		scene.background = new THREE.Color("#050505")
		const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 10000)
		camera.position.set(10, 10, 10)
		const renderer = new THREE.WebGLRenderer({ antialias: true })
		container.appendChild(renderer.domElement)
		const controls = new OrbitControls(camera, renderer.domElement)
		controls.enableDamping = true
		controls.dampingFactor = 0.05

		scene.add(new THREE.AmbientLight(0xffffff, 1.4))
		const light = new THREE.DirectionalLight(0xffffff, 2)
		light.position.set(10, 10, 10)
		scene.add(light)
		scene.add(new THREE.GridHelper(20, 20, 0x333333, 0x181818))

		for (const [index, mesh] of collectMeshes(geometry).entries()) {
			const buffer = new THREE.BufferGeometry()
			buffer.setAttribute("position", new THREE.BufferAttribute(new Float32Array(mesh.vertices), 3))
			if (mesh.indices?.length) buffer.setIndex(mesh.indices)
			if (mesh.normals?.length) {
				buffer.setAttribute("normal", new THREE.BufferAttribute(new Float32Array(mesh.normals), 3))
			} else {
				buffer.computeVertexNormals()
			}
			const color = COLORS[index % COLORS.length]
			const kind = (mesh.geometry_type ?? mesh.geometryType ?? "mesh").toLowerCase()
			if (kind === "line" || kind === "lines") {
				scene.add(new THREE.LineSegments(buffer, new THREE.LineBasicMaterial({ color })))
			} else if (kind === "point" || kind === "points") {
				scene.add(new THREE.Points(buffer, new THREE.PointsMaterial({ color, size: 0.1 })))
			} else {
				scene.add(new THREE.Mesh(buffer, new THREE.MeshStandardMaterial({ color, side: THREE.DoubleSide })))
			}
		}

		const resize = () => {
			const width = Math.max(container.clientWidth, 1)
			const height = Math.max(container.clientHeight, 1)
			renderer.setSize(width, height, false)
			camera.aspect = width / height
			camera.updateProjectionMatrix()
		}
		const observer = new ResizeObserver(resize)
		observer.observe(container)
		resize()

		let frame = 0
		const render = () => {
			controls.update()
			renderer.render(scene, camera)
			frame = requestAnimationFrame(render)
		}
		render()

		return () => {
			cancelAnimationFrame(frame)
			observer.disconnect()
			controls.dispose()
			renderer.dispose()
			renderer.domElement.remove()
		}
	}, [geometry])

	return <div ref={containerRef} style={{ width: 420, height: 300, overflow: "hidden" }} />
}

export default Viewer
`

export const builtInNodeTemplates: BuiltInNodeTemplate[] = [
	{
		code: viewerCode,
		defaultDimensions: { width: 420, height: 340 },
		description: "Display all connected geometry in a 3D viewport.",
		id: "viewer",
		inputs: {
			geometry: {
				description: "Geometry to display",
				required: false,
				type: "geometry",
			},
		},
		keywords: ["3d", "display", "geometry", "output", "preview", "visualize"],
		label: "Viewer",
		metadata: {
			built_in_kind: "viewer",
			description: "Display all connected geometry in a 3D viewport.",
		},
		outputs: [],
		type: "react",
	},
	{
		code: `from oblique import Point3d
from oblique.models import point

def point_input(value: Point3d = {"x": 0, "y": 0, "z": 0}) -> Point3d:
	"""Provide an editable three-dimensional point.

	Args:
		value: Point coordinates.

	Returns:
		The configured point.
	"""
	return point(value)
`,
		defaultDimensions: { width: 260, height: 104 },
		description: "Provide an editable three-dimensional point.",
		id: "point",
		inputs: {
			value: {
				description: "Point coordinates",
				required: false,
				type: "Point3d",
				value: { x: 0, y: 0, z: 0 },
			},
		},
		keywords: ["coordinate", "location", "position", "vector", "xyz"],
		label: "Point",
		metadata: {
			built_in_kind: "point",
			description: "Provide an editable three-dimensional point.",
		},
		outputs: ["result"],
		type: "python",
	},
	{
		code: `import builtins

def number_slider(value: float = 0.5, min: float = 0.0, max: float = 1.0) -> float:
	"""Provide a number constrained by an editable range.

	Args:
		value: Current slider value.
		min: Minimum slider value.
		max: Maximum slider value.

	Returns:
		The current value constrained to the configured range.
	"""
	return builtins.max(min, builtins.min(max, value))
`,
		defaultDimensions: { width: 371, height: 104 },
		description: "Provide a number constrained by an editable range.",
		id: "slider",
		inputs: {
			value: {
				description: "Current value",
				required: false,
				type: "float",
				value: 0.5,
			},
			min: {
				description: "Minimum value",
				required: false,
				type: "float",
				value: 0,
			},
			max: {
				description: "Maximum value",
				required: false,
				type: "float",
				value: 1,
			},
		},
		keywords: ["float", "input", "range", "value"],
		label: "Slider",
		metadata: {
			built_in_kind: "slider",
			description: "Provide a number constrained by an editable range.",
		},
		outputs: ["result"],
		type: "python",
	},
	{
		code: `def text_input(value: str = "") -> str:
	"""Provide editable text.

	Args:
		value: Text value.

	Returns:
		The configured text.
	"""
	return value
`,
		defaultDimensions: { width: 240, height: 104 },
		description: "Provide an editable text value.",
		id: "text",
		inputs: {
			value: {
				description: "Text value",
				required: false,
				type: "string",
				value: "",
			},
		},
		keywords: ["field", "input", "string", "text field"],
		label: "Text",
		metadata: {
			built_in_kind: "text",
			description: "Provide an editable text value.",
		},
		outputs: ["result"],
		type: "python",
	},
	{
		code: `def number_input(value: float = 0.0) -> float:
	"""Provide an editable number.

	Args:
		value: Numeric value.

	Returns:
		The configured number.
	"""
	return value
`,
		defaultDimensions: { width: 220, height: 104 },
		description: "Provide an editable numeric value.",
		id: "number",
		inputs: {
			value: {
				description: "Numeric value",
				required: false,
				type: "float",
				value: 0,
			},
		},
		keywords: ["double", "field", "float", "input", "integer", "value"],
		label: "Number",
		metadata: {
			built_in_kind: "number",
			description: "Provide an editable numeric value.",
		},
		outputs: ["result"],
		type: "python",
	},
	{
		code: `def boolean_input(value: bool = False) -> bool:
	"""Provide an editable boolean value.

	Args:
		value: Boolean value.

	Returns:
		The configured boolean.
	"""
	return value
`,
		defaultDimensions: { width: 220, height: 104 },
		description: "Provide an editable true or false value.",
		id: "boolean",
		inputs: {
			value: {
				description: "Boolean value",
				required: false,
				type: "bool",
				value: false,
			},
		},
		keywords: ["bool", "checkbox", "input", "switch", "toggle"],
		label: "Boolean",
		metadata: {
			built_in_kind: "boolean",
			description: "Provide an editable true or false value.",
		},
		outputs: ["result"],
		type: "python",
	},
	{
		code: `def momentary_button(pressed: bool = False) -> bool:
	"""Provide a momentary button state.

	Args:
		pressed: Whether the button is currently pressed.

	Returns:
		True while the button is pressed.
	"""
	return pressed
`,
		defaultDimensions: { width: 220, height: 104 },
		description: "Emit true while the button is pressed.",
		id: "button",
		inputs: {
			pressed: {
				description: "Pressed state",
				required: false,
				type: "bool",
				value: false,
			},
		},
		keywords: ["action", "click", "input", "momentary", "press", "trigger"],
		label: "Button",
		metadata: {
			built_in_kind: "button",
			description: "Emit true while the button is pressed.",
		},
		outputs: ["result"],
		type: "python",
	},
]

const scoreTemplate = (template: BuiltInNodeTemplate, query: string) => {
	const normalized = query.trim().toLowerCase()
	if (!normalized) return 1
	const label = template.label.toLowerCase()
	const description = template.description.toLowerCase()
	const keywords = template.keywords.join(" ").toLowerCase()
	let score = 0
	if (label === normalized) score += 120
	else if (label.startsWith(normalized)) score += 70
	else if (label.includes(normalized)) score += 42
	if (description.includes(normalized)) score += 28
	if (keywords.includes(normalized)) score += 20
	for (const word of normalized
		.split(/\s+/)
		.filter((item) => item.length > 1)) {
		if (label.includes(word)) score += 14
		if (description.includes(word)) score += 9
		if (keywords.includes(word)) score += 8
	}
	return score
}

export const filterBuiltInNodeTemplates = (query: string, limit = 8) =>
	builtInNodeTemplates
		.map((template, index) => ({
			index,
			score: scoreTemplate(template, query),
			template,
		}))
		.filter(({ score }) => score > 0)
		.sort((left, right) => right.score - left.score || left.index - right.index)
		.slice(0, limit)
		.map(({ template }) => template)
